/**
 * <KeyboardShortcuts />
 * --------------------------------------------------------------------------
 * Mounts global keyboard shortcuts and the help cheatsheet overlay.
 *
 * Shortcuts (Mac uses ⌘, Linux/Windows uses Ctrl):
 *   ⌘/Ctrl + K     → Universal patient search
 *   ⌘/Ctrl + N     → Nueva cita rápida (Agenda)
 *   ⌘/Ctrl + 1..7  → Saltar entre las páginas principales
 *   ?              → Mostrar este overlay de ayuda
 *   Esc            → Cerrar overlay/modal
 *
 * The component:
 *  - Skips key handling when the user is typing in an input/textarea/contenteditable
 *  - Adds a floating "?" pill in the bottom-right so the user can discover
 *    the cheatsheet without having to press anything
 *
 * Usage: <KeyboardShortcuts /> rendered ONCE inside <AdminLayout />.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Keyboard, Search, X, ArrowRight, CalendarPlus } from 'lucide-react';
import PatientPicker from '@/components/shared/PatientPicker';

const isMac = typeof navigator !== 'undefined' && /Mac|iPad|iPhone/.test(navigator.platform);
const META_LABEL = isMac ? '⌘' : 'Ctrl';

const NAV_ROUTES = [
  { key: '1', gKey: 'd', label: 'Dashboard / Mi Panel', path: '/admin/dashboard' },
  { key: '2', gKey: 'a', label: 'Agenda', path: '/admin/agenda' },
  { key: '3', gKey: 'p', label: 'Pacientes', path: '/admin/patients' },
  { key: '4', gKey: 'h', label: 'Historia Clínica', path: '/admin/clinical-history' },
  { key: '5', gKey: 'c', label: 'Cirugías', path: '/admin/surgeries' },
  { key: '6', gKey: 'i', label: 'Prótesis de Mama', path: '/admin/breast-implants' },
  { key: '7', gKey: 'r', label: 'Procedimientos', path: '/admin/procedures' },
];

const G_SEQUENCE_TIMEOUT_MS = 1500;

function isTypingInField(e) {
  const t = e.target;
  if (!t) return false;
  if (t.isContentEditable) return true;
  const tag = (t.tagName || '').toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

function Kbd({ children }) {
  return (
    <kbd className="px-2 py-0.5 text-xs font-mono bg-gray-100 border border-gray-300 rounded shadow-sm text-gray-700">
      {children}
    </kbd>
  );
}

export default function KeyboardShortcuts() {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [gPending, setGPending] = useState(false);
  const gTimerRef = useRef(null);

  const openSearch = useCallback(() => {
    setHelpOpen(false);
    setSearchOpen(true);
  }, []);
  const openHelp = useCallback(() => {
    setSearchOpen(false);
    setHelpOpen(true);
  }, []);
  const closeAll = useCallback(() => {
    setSearchOpen(false);
    setHelpOpen(false);
  }, []);

  const clearGPending = useCallback(() => {
    setGPending(false);
    if (gTimerRef.current) {
      clearTimeout(gTimerRef.current);
      gTimerRef.current = null;
    }
  }, []);

  // Action: from the universal search modal, navigate to Agenda with a flag
  // that prefills the new-appointment dialog with the picked patient.
  const handleCreateAppointmentFor = useCallback(
    (patient) => {
      if (!patient?.id) return;
      closeAll();
      const params = new URLSearchParams({
        action: 'new',
        patient_id: patient.id,
        patient_name: patient.name || '',
      });
      navigate(`/admin/agenda?${params.toString()}`);
    },
    [navigate, closeAll]
  );

  useEffect(() => {
    const onKey = (e) => {
      // Always allow ESC to close, even from inside inputs
      if (e.key === 'Escape') {
        if (helpOpen || searchOpen) {
          e.preventDefault();
          closeAll();
        }
        clearGPending();
        return;
      }
      // Skip while user is typing somewhere (so they don't get hijacked)
      if (isTypingInField(e)) {
        clearGPending();
        return;
      }

      const meta = e.metaKey || e.ctrlKey;

      // ── Gmail-style "g" + letter sequence ──────────────────────────────
      // First key: lonely "g" with no modifiers → arm the sequence.
      if (!meta && !e.altKey && !e.shiftKey && (e.key === 'g' || e.key === 'G')) {
        if (!gPending) {
          e.preventDefault();
          setGPending(true);
          if (gTimerRef.current) clearTimeout(gTimerRef.current);
          gTimerRef.current = setTimeout(() => {
            setGPending(false);
            gTimerRef.current = null;
          }, G_SEQUENCE_TIMEOUT_MS);
          return;
        }
      }
      // Second key while pending: navigate if it matches a route or trigger
      // a special action ("g n" → nueva cita rápida). Otherwise cancel.
      if (gPending && !meta && !e.altKey) {
        const k = e.key.toLowerCase();
        if (k === 'n') {
          e.preventDefault();
          navigate('/admin/agenda?action=new');
          closeAll();
          clearGPending();
          return;
        }
        const route = NAV_ROUTES.find((r) => r.gKey === k);
        if (route) {
          e.preventDefault();
          navigate(route.path);
          closeAll();
        }
        clearGPending();
        return;
      }

      if (meta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        openSearch();
        return;
      }
      if (meta && /^[1-7]$/.test(e.key)) {
        const route = NAV_ROUTES.find((r) => r.key === e.key);
        if (route) {
          e.preventDefault();
          navigate(route.path);
          closeAll();
        }
        return;
      }
      if (e.key === '?' && !meta && !e.altKey) {
        e.preventDefault();
        openHelp();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (gTimerRef.current) clearTimeout(gTimerRef.current);
    };
  }, [helpOpen, searchOpen, gPending, openSearch, openHelp, closeAll, clearGPending, navigate]);

  return (
    <>
      {/* Floating discovery icon — small, circular, sits above the footer
          copyright and the connection indicator so it never overlaps them. */}
      <button
        type="button"
        onClick={openHelp}
        className="fixed bottom-14 right-3 z-[60] inline-flex items-center justify-center h-9 w-9 rounded-full bg-white border border-gray-200 shadow-md text-gray-500 hover:bg-brand-soft/40 hover:border-brand-medium hover:text-brand-navy transition-colors"
        title="Atajos de teclado (pulsa ?)"
        aria-label="Mostrar atajos de teclado"
        data-testid="kbd-shortcuts-toggle"
      >
        <Keyboard size={16} />
        <span className="sr-only">Atajos de teclado</span>
      </button>

      {/* Universal patient search modal */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center pt-[12vh] px-4"
          onClick={closeAll}
          data-testid="kbd-search-modal"
        >
          <div
            className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b">
              <Search size={16} className="text-gray-500" />
              <p className="text-sm font-semibold text-brand-navy flex-1">
                Buscar paciente
              </p>
              <span className="text-[11px] text-gray-400">
                Esc para cerrar
              </span>
            </div>
            <div className="p-4">
              <PatientPicker
                value={null}
                onChange={(p) => {
                  if (p?.id) {
                    closeAll();
                    navigate(`/admin/clinical-history?patient=${p.id}`);
                  }
                }}
                onSecondaryAction={handleCreateAppointmentFor}
                autoFocus
                testId="kbd-search-picker"
                placeholder="Empieza a escribir el nombre, DNI o NHC…"
              />
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-gray-500">
                <span className="inline-flex items-center gap-1.5">
                  <ArrowRight size={11} /> <Kbd>Enter</Kbd> abrir historia
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CalendarPlus size={11} /> <Kbd>{META_LABEL}</Kbd>+<Kbd>Enter</Kbd> nueva cita
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tiny indicator when "g" sequence is armed */}
      {gPending && (
        <div
          className="fixed bottom-[108px] right-3 z-[60] inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-navy text-white text-xs shadow-lg animate-in slide-in-from-bottom-2"
          data-testid="kbd-g-pending"
        >
          <Kbd>g</Kbd>
          <span>… esperando segunda tecla</span>
        </div>
      )}

      {/* Help cheatsheet overlay */}
      {helpOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4"
          onClick={closeAll}
          data-testid="kbd-help-modal"
        >
          <div
            className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-5 py-3 border-b">
              <Keyboard size={18} className="text-brand-navy" />
              <h3 className="font-semibold text-brand-navy flex-1">
                Atajos de teclado
              </h3>
              <button
                type="button"
                onClick={closeAll}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Cerrar"
                data-testid="kbd-help-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
              <section>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Acciones rápidas
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center justify-between">
                    <span className="text-gray-700">Buscar paciente universal</span>
                    <span className="flex items-center gap-1">
                      <Kbd>{META_LABEL}</Kbd> + <Kbd>K</Kbd>
                    </span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-gray-700">Nueva cita rápida (Agenda)</span>
                    <span className="flex items-center gap-1">
                      <Kbd>g</Kbd> <Kbd>n</Kbd>
                    </span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-gray-700">
                      Forzar cita (Agenda)
                      <span className="block text-[11px] text-gray-500">
                        Usa la fecha activa de la vista
                      </span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Kbd>Alt</Kbd> + <Kbd>F</Kbd>
                    </span>
                  </li>
                  <li className="flex items-center justify-between gap-3">
                    <span className="text-gray-700">
                      Crear cita desde el buscador
                      <span className="block text-[11px] text-gray-500">
                        Selecciona un paciente con ↑/↓ y luego ⌘/Ctrl+Enter
                      </span>
                    </span>
                    <span className="flex items-center gap-1 shrink-0">
                      <Kbd>{META_LABEL}</Kbd> + <Kbd>Enter</Kbd>
                    </span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-gray-700">Mostrar este panel</span>
                    <Kbd>?</Kbd>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-gray-700">Cerrar overlay/modal</span>
                    <Kbd>Esc</Kbd>
                  </li>
                </ul>
              </section>

              <section>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Navegación rápida (estilo Gmail)
                </p>
                <p className="text-[11px] text-gray-500 mb-2">
                  Pulsa <Kbd>g</Kbd> y luego la letra. Más rápido que ⌘ + número.
                </p>
                <ul className="space-y-2 text-sm">
                  {NAV_ROUTES.map((r) => (
                    <li key={r.key} className="flex items-center justify-between">
                      <span className="text-gray-700">{r.label}</span>
                      <span className="flex items-center gap-2">
                        <span className="flex items-center gap-1">
                          <Kbd>g</Kbd> <Kbd>{r.gKey}</Kbd>
                        </span>
                        <span className="text-gray-300">·</span>
                        <span className="flex items-center gap-1">
                          <Kbd>{META_LABEL}</Kbd> + <Kbd>{r.key}</Kbd>
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>

              <p className="text-[11px] text-gray-400">
                Los atajos se ignoran cuando estás escribiendo en un campo de
                texto, para no interferir con tu trabajo.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
